import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiPerformanceApi } from '../lib/api';
import {
  CpuChipIcon,
  ShieldCheckIcon,
  ClockIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

type Period = '24h' | '7d' | '30d';

export default function AiPerformancePage() {
  const [period, setPeriod] = useState<Period>('30d');
  const queryClient = useQueryClient();

  const { data: dashboard, isLoading } = useQuery({
    queryKey: ['ai-performance-dashboard', period],
    queryFn: () => aiPerformanceApi.getDashboard(period).then((r) => r.data),
  });

  const repairMutation = useMutation({
    mutationFn: () => aiPerformanceApi.repairAuditChain(),
    onSuccess: (res: any) => {
      const { repaired, total } = res.data || {};
      toast.success(`Chain repaired — ${repaired} of ${total} entries fixed`);
      queryClient.invalidateQueries({ queryKey: ['ai-performance-dashboard'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to repair audit chain');
    },
  });

  const { data: accuracy } = useQuery({
    queryKey: ['ai-performance-accuracy', period],
    queryFn: () => aiPerformanceApi.getAccuracy(period).then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-navy-900">AI Performance</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
              <div className="h-8 bg-gray-200 rounded w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const providerHealth = dashboard?.providerHealth || {};
  const auditIntegrity = dashboard?.auditIntegrity;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">AI Performance</h1>
          <p className="text-sm text-gray-500 mt-1">
            AI accuracy, review metrics, provider health, and audit integrity
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['24h', '7d', '30d'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-sm rounded-md ${
                period === p
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          label="AI Accuracy"
          value={dashboard?.totalAiCalls > 0 && dashboard?.accuracy != null ? `${dashboard.accuracy}%` : 'N/A'}
          description={dashboard?.totalAiCalls > 0 ? 'AI flags confirmed by human reviewers' : 'No AI calls in this period'}
          icon={<ShieldCheckIcon className="h-5 w-5 text-teal-600" />}
          color="teal"
        />
        <MetricCard
          label="False Positive Rate"
          value={dashboard?.totalAiCalls > 0 && dashboard?.falsePositiveRate != null ? `${dashboard.falsePositiveRate}%` : 'N/A'}
          description={dashboard?.totalAiCalls > 0 ? 'AI flags overridden by reviewers' : 'No AI calls in this period'}
          icon={<ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />}
          color="amber"
        />
        <MetricCard
          label="Review Turnaround"
          value={dashboard?.medianReviewTimeHours != null ? `${dashboard.medianReviewTimeHours}h` : 'N/A'}
          description="Median time to human disposition"
          icon={<ClockIcon className="h-5 w-5 text-blue-600" />}
          color="blue"
        />
        <MetricCard
          label="AI Calls"
          value={dashboard?.totalAiCalls?.toLocaleString() || '0'}
          description={dashboard?.totalAiCalls > 0 && dashboard?.successRate != null ? `${dashboard.successRate}% success rate` : 'No data yet'}
          icon={<CpuChipIcon className="h-5 w-5 text-navy-600" />}
          color="navy"
        />
      </div>

      {/* Provider Health */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-navy-900 mb-4">Provider Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(['openai', 'anthropic', 'gemini'] as const).map((provider) => {
            const health = providerHealth[provider];
            const status = health?.status || 'unknown';
            const failures = health?.consecutiveFailures || 0;

            return (
              <div
                key={provider}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  status === 'healthy'
                    ? 'border-green-200 bg-green-50'
                    : status === 'degraded'
                    ? 'border-amber-200 bg-amber-50'
                    : status === 'down'
                    ? 'border-red-200 bg-red-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div>
                  <p className="font-medium text-navy-900">
                    {provider === 'openai' ? 'OpenAI' : provider === 'anthropic' ? 'Anthropic' : 'Google Gemini'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {provider === 'openai' ? 'GPT-4o' : provider === 'anthropic' ? 'Claude Sonnet 4' : 'Gemini 1.5 Pro'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {status === 'healthy' && <CheckCircleIcon className="h-5 w-5 text-green-600" />}
                  {status === 'degraded' && <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />}
                  {status === 'down' && <XCircleIcon className="h-5 w-5 text-red-600" />}
                  <span className={`text-sm font-medium capitalize ${
                    status === 'healthy' ? 'text-green-700' : status === 'degraded' ? 'text-amber-700' : 'text-red-700'
                  }`}>
                    {status}
                  </span>
                  {failures > 0 && (
                    <span className="text-xs text-gray-500">({failures} failures)</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Accuracy by Module */}
      {accuracy && accuracy.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy-900 mb-4">Accuracy by Use Case</h2>
          <div className="space-y-3">
            {accuracy.map((item: any) => (
              <div key={item.useCase} className="flex items-center gap-4">
                <div className="w-48 text-sm text-gray-700 truncate">{formatUseCase(item.useCase)}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-100 rounded-full h-3">
                      <div
                        className="bg-teal-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${item.accuracy || 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-navy-900 w-14 text-right">
                      {item.accuracy != null ? `${item.accuracy}%` : '—'}
                    </span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 w-20 text-right">
                  {item.totalCalls} reviews
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit Integrity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-navy-900">Audit Trail Integrity</h2>
          {auditIntegrity && !auditIntegrity.isValid && (
            <button
              onClick={() => {
                if (confirm('Repair the audit chain? This will recompute hashes for all entries to restore chain integrity. Existing audit data is not modified — only the cryptographic hashes are recalculated.')) {
                  repairMutation.mutate();
                }
              }}
              disabled={repairMutation.isPending}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
            >
              <WrenchScrewdriverIcon className="h-4 w-4" />
              {repairMutation.isPending ? 'Repairing...' : 'Repair Chain'}
            </button>
          )}
        </div>
        {auditIntegrity ? (
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              {auditIntegrity.isValid ? (
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              ) : (
                <XCircleIcon className="h-6 w-6 text-red-600" />
              )}
              <span className={`text-lg font-semibold ${auditIntegrity.isValid ? 'text-green-700' : 'text-red-700'}`}>
                {auditIntegrity.isValid ? 'Chain Valid' : 'Chain Broken'}
              </span>
            </div>
            <div
              className="text-sm text-gray-500"
              title="Total platform-wide audit log entries cryptographically verified — the hash chain spans the entire database, not just your team."
            >
              {auditIntegrity.totalChecked.toLocaleString()} platform entries verified
            </div>
            <div
              className="text-sm text-gray-500"
              title="Audit log events recorded for your team specifically."
            >
              {auditIntegrity.totalEvents.toLocaleString()} events for your team
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No audit data available yet</p>
        )}
        <p className="text-xs text-gray-400 mt-3">
          SHA-256 hash-chained audit log. The chain spans every entry across the platform
          so any tampering — even outside your team — is detectable. Immutable: entries
          are never deleted.
        </p>
      </div>

      {/* Examiner Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <ChartBarIcon className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">For Examiner Review</p>
            <p className="text-xs text-blue-700 mt-1">
              This dashboard provides real-time visibility into AI model performance. All AI in Clarid is detection-only —
              deterministic rules make compliance decisions, and human compliance officers approve everything. Provider health,
              accuracy metrics, and audit trail integrity are continuously monitored.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  description,
  icon,
}: {
  label: string;
  value: string;
  description: string;
  icon: React.ReactNode;
  color?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500">{label}</span>
        {icon}
      </div>
      <p className="text-2xl font-bold text-navy-900">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
  );
}

function formatUseCase(useCase: string): string {
  const map: Record<string, string> = {
    'text-extraction': 'Text Extraction (OCR)',
    'image-feature-analysis': 'Image Analysis',
    'risky-phrase-detection': 'Risky Phrase Detection',
    'hmda-edit-explanation': 'HMDA Edit Explanations',
    'fair-lending-content-screening': 'Fair Lending Screening',
    'compliance-summary': 'Compliance Summary',
    'chatbot': 'AI Chatbot',
    'policy-gap-analysis': 'Policy Gap Analysis',
  };
  return map[useCase] || useCase;
}
